import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UserPlus, Send } from "lucide-react";
import { toast } from "sonner";
import { submitInfluencerRequest } from "../lib/api";

const CATS = [
  { v: "creator", l: "Creator" },
  { v: "bollywood", l: "Bollywood" },
  { v: "reality", l: "Reality" },
  { v: "music", l: "Music" },
  { v: "athletes", l: "Athlete" },
];

export default function RequestInfluencerDialog({ open, onClose, onSubmitted, initialName = "" }) {
  const [name, setName] = useState(initialName);
  const [handle, setHandle] = useState("");
  const [category, setCategory] = useState("creator");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !handle.trim()) return toast.error("Add name and Instagram handle.");
    setBusy(true);
    try {
      const r = await submitInfluencerRequest({ name: name.trim(), handle: handle.trim(), category, reason: reason.trim() });
      if (r.already) toast.success("Counted your vote on existing request!");
      else toast.success("Request posted — fans can upvote it now 🙌");
      onSubmitted && onSubmitted(r);
      setName(""); setHandle(""); setReason(""); setCategory("creator");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center p-4"
        data-testid="request-influencer-dialog"
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="relative w-full max-w-md glass-strong rounded-3xl p-5 border border-white/10"
        >
          <button onClick={onClose} data-testid="req-close"
            className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full hover:bg-white/10" aria-label="close">
            <X className="w-4 h-4" />
          </button>
          <div className="w-11 h-11 rounded-2xl grid place-items-center" style={{ background: "#B026FF22", border: "1px solid #B026FF55" }}>
            <UserPlus className="w-5 h-5" style={{ color: "#B026FF" }} />
          </div>
          <h3 className="font-display text-2xl font-bold mt-3">Request an Influencer</h3>
          <p className="text-sm text-white/60 mt-1">Don't see your fav on Fanpuff? Drop their details. If enough fans upvote it, we'll add them.</p>

          <div className="mt-4 space-y-3">
            <Field label="Name">
              <input
                data-testid="req-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Zeenat Aman"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#B026FF]/50"
              />
            </Field>
            <Field label="Instagram / handle">
              <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
                <span className="text-white/40">@</span>
                <input
                  data-testid="req-handle" value={handle} onChange={e => setHandle(e.target.value.replace(/^@/, ""))}
                  placeholder="theoneandonlyzeenat"
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>
            </Field>
            <Field label="Category">
              <div className="flex gap-2 flex-wrap">
                {CATS.map(c => (
                  <button
                    key={c.v}
                    data-testid={`req-cat-${c.v}`}
                    onClick={() => setCategory(c.v)}
                    className="chip px-3 py-1.5 text-xs"
                    style={category === c.v
                      ? { background: "linear-gradient(90deg,#B026FF,#00F0FF)", color: "#000", border: "1px solid transparent" }
                      : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
                  >{c.l}</button>
                ))}
              </div>
            </Field>
            <Field label="Why do fans love them? (optional)">
              <textarea
                data-testid="req-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={280}
                placeholder="e.g. 14M followers, Cannes regular, OTT breakout…"
                className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#B026FF]/50"
              />
            </Field>
          </div>
          <button
            onClick={submit} disabled={busy} data-testid="req-submit"
            className="mt-4 w-full py-3 rounded-full font-bold text-black inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
          >
            <Send className="w-4 h-4" /> Post request
          </button>
          <p className="mt-3 text-[10px] text-white/40">Submitting gives you +1 vote. Highest-voted requests get added fastest.</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-white/50">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
